import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import CurrencyInput from '../../CurrencyInput/components/CurrencyInput';

// Forecasting aid: reuses the ticket build's all-in engine (POST build_ticket,
// absorb_fees) so the offer all-in matches checkout exactly. Two directions:
//  - "all_in": booker knows what the fan should pay (e.g. $100) -> face value (net after absorbed fees)
//  - "face":   booker knows the face value they need -> all-in price to charge
// Absorb fees is assumed (the "$100 all in" case). On-screen only; nothing is saved.

const labelStyle = {
  display: 'block',
  fontSize: '13px',
  fontWeight: 500,
  color: 'var(--sl-text-tertiary)',
  marginBottom: '6px'
};

const rowStyle = (emphasis) => ({
  display: 'flex',
  justifyContent: 'space-between',
  padding: '8px 0',
  fontSize: emphasis ? '15px' : '14px',
  fontWeight: emphasis ? 600 : 400,
  color: emphasis ? 'var(--sl-text-primary)' : 'var(--sl-text-tertiary)',
  borderTop: emphasis ? '1px solid var(--sl-border-secondary)' : 'none'
});

const inputStyle = {
  fontSize: '14px',
  backgroundColor: 'var(--sl-bg-primary)',
  color: 'var(--sl-text-primary)',
  borderColor: 'var(--sl-border-primary)'
};

const radioLabelStyle = {
  fontSize: '14px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  color: 'var(--sl-text-secondary)'
};

const fmt = (n) => '$' + (Number(n) || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2, maximumFractionDigits: 2
});

// Parse one card[0] result into the numbers we display.
export const parseResult = (card) => {
  const r = card && card[0] ? card[0] : {};
  const allIn = (parseFloat(r.final_cost) || 0) + (parseFloat(r.taxes) || 0);
  return {
    fees: parseFloat(r.fees) || 0,
    opendate_service_fee: parseFloat(r.opendate_service_fee) || 0,
    organizer_service_fee: parseFloat(r.organizer_service_fee) || 0,
    processing_fee: parseFloat(r.processing_fee) || 0,
    rebate_fee: parseFloat(r.rebate_fee) || 0,
    // Taken out of face value, so it is deliberately NOT part of `fees` (the buyer
    // never pays it). Any net has to subtract it separately — see netOf().
    rebate_face_value_fee: parseFloat(r.rebate_face_value_fee) || 0,
    taxes: parseFloat(r.taxes) || 0,
    faceValue: parseFloat(r.final_cost) || 0, // absorb mode: final_cost == entered price
    allIn
  };
};

// What the artist/venue side actually keeps per ticket. A face-value rebate comes
// out of the ticket price without ever touching `fees`, so deriving the net as
// `face - fees` overstates it by the whole rebate.
export const netOf = (r) => (r ? r.faceValue - r.fees - r.rebate_face_value_fee : 0);

// Reverse-solve: find the all-in price whose net (face value) equals `target`.
// net == price - fees, and fees depend on price, so iterate. `compute(price)`
// returns a parseResult() object. Converges within a few rounds for % fees.
export const solveAllInForFace = async (compute, target) => {
  let price = target;
  let last;
  for (let i = 0; i < 12; i++) {
    last = await compute(price);
    const net = netOf(last);
    const diff = target - net;
    if (Math.abs(diff) < 0.005) break;
    price = +(price + diff).toFixed(2);
  }
  return last;
};

const AllInPriceModal = ({ team, calendarEventId, csrfToken, initialPrice, onClose }) => {
  const [mode, setMode] = useState('all_in'); // 'all_in' | 'face'
  const [input, setInput] = useState(Number(initialPrice) > 0 ? Number(initialPrice).toFixed(2) : '');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // One round-trip to the existing ticket-build engine, absorb fees on. When the
  // offer has a linked event we pass calendar_event_id for event-accurate fees;
  // otherwise the engine falls back to the team's fee rule set.
  // ponytail: no taxable_items passed, so taxes are 0 (fees-only all-in). Pass
  // the event's manual taxes here if a venue needs tax-inclusive all-in.
  const compute = useCallback((price) => {
    const ticketType = { price, free: false, absorb_fees: true, round_up: false };
    if (calendarEventId) ticketType.calendar_event_id = calendarEventId;

    return axios.post(`/teams/${team.id}/ticket_types/build_ticket`, {
      ticket_type: { ticket_types: [ticketType] }
    }, {
      headers: { 'X-CSRF-TOKEN': csrfToken }
    }).then(({ data }) => parseResult(data.card));
  }, [team.id, calendarEventId, csrfToken]);

  useEffect(() => {
    const amount = parseFloat(String(input).replace(/[$,]/g, ''));
    if (!amount || amount <= 0) { setResult(null); setError(null); return; }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const run = () => mode === 'all_in'
      ? compute(amount)
      : solveAllInForFace(compute, amount);

    const timer = setTimeout(() => {
      run()
        .then((res) => { if (!cancelled) setResult(res); })
        .catch(() => { if (!cancelled) setError('Could not calculate. Try again.'); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 350);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [input, mode, compute]);

  const netFace = netOf(result);

  return (
    <div>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '20px 24px', borderBottom: '1px solid var(--sl-border-secondary)'
      }}>
        <h5 style={{ margin: 0, fontWeight: 600, fontSize: '18px', color: 'var(--sl-text-primary)' }}>All-In Price Calculator</h5>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px',
          color: 'var(--sl-text-quaternary)', padding: '4px 8px', lineHeight: 1
        }}>&times;</button>
      </div>

      <div style={{ padding: '20px 24px' }}>
        <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
          <label style={radioLabelStyle}>
            <input type="radio" checked={mode === 'all_in'} onChange={() => setMode('all_in')} />
            I know the all-in price
          </label>
          <label style={radioLabelStyle}>
            <input type="radio" checked={mode === 'face'} onChange={() => setMode('face')} />
            I know the face value
          </label>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>
            {mode === 'all_in' ? 'All-in price (what the fan pays)' : 'Face value (your net per ticket)'}
          </label>
          <div style={{ width: '220px' }}>
            <CurrencyInput
              className="form-control"
              value={input}
              onChange={(e) => setInput(e.target.value.replace(/[$,]/g, ''))}
              onFocus={(e) => e.target.select()}
              placeholder="$0.00"
              style={inputStyle}
              autoFocus
            />
          </div>
        </div>

        {error && <p style={{ color: 'var(--sl-text-error)', fontSize: '13px' }}>{error}</p>}

        {result && !error && (
          <div style={{ opacity: loading ? 0.5 : 1 }}>
            <div style={rowStyle()}>
              <span>{mode === 'all_in' ? 'All-in price (fan pays)' : 'All-in price to charge'}</span>
              <span style={{ fontWeight: 600, color: 'var(--sl-text-primary)' }}>{fmt(result.allIn)}</span>
            </div>
            {(result.opendate_service_fee + result.organizer_service_fee) > 0 && (
              <div style={rowStyle()}><span>&minus; Total Ticket Fees</span><span>{fmt(result.opendate_service_fee + result.organizer_service_fee)}</span></div>
            )}
            {result.processing_fee > 0 && (
              <div style={rowStyle()}><span>&minus; Processing Fee</span><span>{fmt(result.processing_fee)}</span></div>
            )}
            {result.rebate_face_value_fee > 0 && (
              <div style={rowStyle()}><span>&minus; Rebate (from face value)</span><span>{fmt(result.rebate_face_value_fee)}</span></div>
            )}
            {result.rebate_fee > 0 && (
              <div style={rowStyle()}><span>&minus; Rebate (absorbed)</span><span>{fmt(result.rebate_fee)}</span></div>
            )}
            <div style={rowStyle(true)}>
              <span>Face value (net per ticket)</span>
              <span>{fmt(netFace)}</span>
            </div>
          </div>
        )}

        <p style={{ fontSize: '12px', color: 'var(--sl-text-quaternary)', marginTop: '16px', marginBottom: 0 }}>
          Assumes fees are absorbed (the fan pays a clean all-in price). Forecasting aid only &mdash; this does not change the offer.
        </p>
      </div>
    </div>
  );
};

AllInPriceModal.propTypes = {
  team: PropTypes.object.isRequired,
  calendarEventId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  csrfToken: PropTypes.string.isRequired,
  initialPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onClose: PropTypes.func.isRequired
};

export default AllInPriceModal;
